import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { 
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { toast } from 'sonner';
import { 
  ShoppingCart,
  Eye,
  ExternalLink,
  CheckCircle,
  Clock,
  RefreshCw,
  PoundSterling,
  Rocket,
  Download,
  Loader2
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WebsiteOrdered = () => {
  const { token } = useAuth();
  const [websites, setWebsites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [makingLive, setMakingLive] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchWebsites();
  }, []);

  const fetchWebsites = async () => {
    try {
      const response = await axios.get(`${API}/websites`, { headers });
      // Filter to only show ordered status
      const orderedWebsites = response.data.filter(w => w.status === 'ordered');
      setWebsites(orderedWebsites);
    } catch (error) {
      console.error('Failed to fetch websites:', error);
      toast.error('Failed to load websites');
    } finally {
      setLoading(false);
    }
  };

  const handleMakeLive = async (websiteId) => {
    setMakingLive(websiteId);
    try {
      await axios.patch(`${API}/websites/${websiteId}/go-live`, {}, { headers });
      toast.success('Website is now live!');
      fetchWebsites();
    } catch (error) {
      toast.error('Failed to make website live');
    } finally {
      setMakingLive(null);
    }
  };

  const handleDownload = async (website) => {
    setDownloading(website.id);
    try {
      const response = await axios.get(`${API}/websites/preview/${website.id}`);
      const { html, css } = response.data;
      
      const fullHtml = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${website.business_name}</title>
    <style>
${css || ''}
    </style>
</head>
<body>
${html || ''}
</body>
</html>`;

      const blob = new Blob([fullHtml], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${website.subdomain}.html`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Website downloaded!');
    } catch (error) {
      toast.error('Failed to download website');
    } finally {
      setDownloading(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="website-ordered-page">
      <Card className="bg-card/50 border-white/5">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-green-500" />
              Orders ({websites.length})
            </CardTitle>
            <CardDescription>
              Clients who have purchased - ready to deploy their websites
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={fetchWebsites}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {websites.length === 0 ? (
            <div className="py-16 text-center">
              <ShoppingCart className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium mb-2">No orders yet</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                When a client purchases via Stripe, their order will appear here ready for deployment.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {websites.map((website) => {
                // Format price based on plan type
                const getPriceDisplay = () => {
                  switch(website.plan_type) {
                    case 'monthly':
                      return '£59.99/month subscription';
                    case 'setup_hosting':
                      return '£500 + £24.99/month';
                    case 'full_purchase':
                      return '£1,399.99 one-time';
                    default:
                      return website.plan_type || 'Unknown';
                  }
                };
                
                return (
                <div
                  key={website.id}
                  className="bg-background/50 border border-green-500/20 rounded-xl p-5"
                  data-testid={`ordered-${website.id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold text-lg">{website.business_name}</h4>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-400 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Paid
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground space-y-1">
                        <a 
                          href={`https://tryweb.co.uk/preview/${website.subdomain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-primary hover:underline"
                        >
                          <ExternalLink className="w-3 h-3" />
                          tryweb.co.uk/preview/{website.subdomain}
                        </a>
                        <p className="flex items-center gap-2">
                          <PoundSterling className="w-3 h-3" />
                          {getPriceDisplay()}
                        </p>
                        <p className="flex items-center gap-2">
                          <Clock className="w-3 h-3" />
                          Ordered: {formatDate(website.updated_at || website.created_at)}
                        </p>
                        {website.client_email && (
                          <p className="text-green-400">
                            Client: {website.client_email}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          window.open(`https://tryweb.co.uk/preview/${website.subdomain}`, '_blank');
                        }}
                        className="gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        Preview
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(website)}
                        disabled={downloading === website.id}
                        className="gap-2"
                      >
                        {downloading === website.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                        Download
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleMakeLive(website.id)}
                        disabled={makingLive === website.id}
                        className="gap-2 bg-green-600 hover:bg-green-700"
                      >
                        {makingLive === website.id ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            Deploying...
                          </>
                        ) : (
                          <>
                            <Rocket className="w-4 h-4" />
                            Make Live
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )})}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WebsiteOrdered;
